import React from 'react';
import _ from 'underscore';

import { Table } from '../../../table/table';
import { CheckableTag } from './folder-tags-dialog';
import { HeaderRow } from './table-header-row';
import { TagRow } from './table-row';

interface Props {
  tags: CheckableTag[];
  disabled: boolean;
  onSelectionChanged: (selectedTagIds: string[]) => void;
}

type TableState = {
  headerChecked?: boolean;
  tags: CheckableTag[];
};
type TableAction =
  | { type: 'INIT'; tags: CheckableTag[] }
  | { type: 'TOGGLE_ALL'; checked: boolean }
  | { type: 'TOGGLE_HEADER'; checked: boolean }
  | { type: 'TOGGLE_TAG'; id: string; checked: boolean };

const TagsTable = (props: Props) => {
  const lastSelectedIds = React.useRef<string[]>([]);
  const [table, dispatchTable] = React.useReducer(tableReducer, {
    headerChecked: false,
    tags: [],
  });

  React.useEffect(() => {
    dispatchTable({
      type: 'INIT',
      tags: props.tags,
    });
  }, [props.tags]);

  const selectionChanged = (list1: string[], list2: string[]) =>
    list1.length !== list2.length || _.intersection(list1, list2).length !== list1.length;

  React.useEffect(() => {
    // Only call changed callback when list has changed.
    const selectedIds = table.tags.filter((x) => x.checked).map((x) => x.id);
    if (selectionChanged(selectedIds, lastSelectedIds.current)) {
      props.onSelectionChanged(selectedIds);
      lastSelectedIds.current = selectedIds;
    }
  }, [props, table.tags]);

  function tableReducer(state: TableState, action: TableAction): TableState {
    switch (action.type) {
      case 'INIT': {
        return {
          headerChecked: calcHeaderCheckBoxState(action.tags),
          tags: action.tags,
        };
      }

      case 'TOGGLE_ALL':
        return {
          headerChecked: action.checked,
          tags: state.tags.map((x) =>
            x.checked !== action.checked
              ? {
                  ...x,
                  checked: action.checked,
                }
              : x,
          ),
        };

      case 'TOGGLE_HEADER': {
        return {
          ...state,
          headerChecked: !state.headerChecked,
          tags: state.tags.map((x) => ({
            ...x,
            checked: !state.headerChecked,
          })),
        };
      }

      case 'TOGGLE_TAG': {
        const tags = state.tags.map((x) =>
          x.id === action.id
            ? {
                ...x,
                checked: action.checked,
              }
            : x,
        );

        return {
          headerChecked: calcHeaderCheckBoxState(tags),
          tags,
        };
      }
    }
  }

  function calcHeaderCheckBoxState(tags: CheckableTag[]) {
    const noneChecked = tags.every((x) => !x.checked);
    const allChecked = tags.every((x) => x.checked);

    return noneChecked || allChecked ? allChecked : undefined;
  }

  const onHeaderSelectionChanged = React.useCallback((checked: boolean) => {
    dispatchTable({
      type: 'TOGGLE_ALL',
      checked,
    });
  }, []);

  const onFolderSelectionChanged = React.useCallback((checked: boolean, tag: CheckableTag) => {
    dispatchTable({
      type: 'TOGGLE_TAG',
      id: tag.id,
      checked,
    });
  }, []);

  const onFolderClick = React.useCallback((tag: CheckableTag) => {
    dispatchTable({
      type: 'TOGGLE_TAG',
      id: tag.id,
      checked: !tag.checked,
    });
  }, []);

  return (
    <Table>
      <Table.Head>
        <HeaderRow
          checked={table.headerChecked}
          disabled={props.disabled}
          onSelectionChanged={onHeaderSelectionChanged}
        />
      </Table.Head>

      <Table.Body>
        {table.tags.map((tag) => (
          <TagRow
            checked={tag.checked}
            disabled={props.disabled}
            key={tag.id}
            tag={tag}
            onClick={onFolderClick}
            onSelectionChanged={onFolderSelectionChanged}
          />
        ))}
      </Table.Body>
    </Table>
  );
};

export { TagsTable };
